import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import { BalanceSchema } from './use-load-aged-balance';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from './utils';

const URL = '/group/analytics';

type LoadGroupAnalyticsVariables = { date: string; companies?: ReadonlyArray<string> };

const CompanyMonthsSchema = z.array(
  z.object({ company: z.string(), months: z.record(z.iso.date(), z.number()) }),
);

export const GroupAnalyticsSchema = z.object({
  agedBalance: BalanceSchema.nullable(),
  companyDsos: CompanyMonthsSchema,
  companyCeis: CompanyMonthsSchema,
  computedAt: z.string().nullable(),
  excludedCompanies: z.array(z.string()),
  contributions: z.array(
    z.object({
      company: z.string(),
      computedAt: z.string().nullable(),
      agedBalance: BalanceSchema,
    }),
  ),
});

export type GroupAnalytics = z.infer<typeof GroupAnalyticsSchema>;

async function loadGroupAnalyticsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadGroupAnalyticsVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: variables });

  const parseResult = GroupAnalyticsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupAnalytics');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadGroupAnalytics(variables: LoadGroupAnalyticsVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<GroupAnalytics, ApiError>({
    queryKey: ['group-analytics', variables],
    queryFn: () => loadGroupAnalyticsQueryFn(axiosInstance, variables),
  });

  return addResourceNameToQueryResult<GroupAnalytics, ApiError, 'groupAnalytics'>(
    'groupAnalytics',
    queryResult,
  );
}
